import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { User01Icon, CheckVerified02Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

// ============================================================================
// Avatar Types (Untitled UI)
// ============================================================================

export type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
export type AvatarShape = 'circle' | 'rounded' | 'square'
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away'

// ============================================================================
// Avatar Styles (Untitled UI Design Tokens)
// ============================================================================

/**
 * Size styles following Untitled UI avatar sizing
 * - xxs: 16px (company icon, very small indicators)
 * - xs: 24px (inline text, compact lists)
 * - sm: 32px (tables, menus, sidebar)
 * - md: 40px (default, cards, headers)
 * - lg: 48px (profile sections)
 * - xl: 56px (profile headers)
 * - 2xl: 64px (hero sections, large profiles)
 */
const sizeStyles: Record<
  AvatarSize,
  { container: string; text: string; icon: string; status: string; statusOffset: string }
> = {
  xxs: {
    container: 'h-4 w-4',
    text: 'text-[8px] font-medium',
    icon: 'h-2.5 w-2.5',
    status: 'h-1.5 w-1.5 ring-1',
    statusOffset: '-bottom-px -right-px',
  },
  xs: {
    container: 'h-6 w-6',
    text: 'text-[10px] font-medium',
    icon: 'h-3.5 w-3.5',
    status: 'h-1.5 w-1.5 ring-1',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
  sm: {
    container: 'h-8 w-8',
    text: 'text-xs font-medium',
    icon: 'h-4 w-4',
    status: 'h-2 w-2 ring-[1.5px]',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
  md: {
    container: 'h-10 w-10',
    text: 'text-sm font-medium',
    icon: 'h-5 w-5',
    status: 'h-2.5 w-2.5 ring-2',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
  lg: {
    container: 'h-12 w-12',
    text: 'text-base font-medium',
    icon: 'h-6 w-6',
    status: 'h-3 w-3 ring-2',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
  xl: {
    container: 'h-14 w-14',
    text: 'text-lg font-semibold',
    icon: 'h-7 w-7',
    status: 'h-3.5 w-3.5 ring-2',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
  '2xl': {
    container: 'h-16 w-16',
    text: 'text-xl font-semibold',
    icon: 'h-8 w-8',
    status: 'h-4 w-4 ring-2',
    statusOffset: '-bottom-0.5 -right-0.5',
  },
}

/**
 * Shape styles for avatar container
 */
const shapeStyles: Record<AvatarShape, string> = {
  circle: 'rounded-full',
  rounded: 'rounded-lg',
  square: 'rounded-none',
}

/**
 * Status indicator colors following Untitled UI semantic colors
 */
const statusColors: Record<AvatarStatus, string> = {
  online: 'bg-success-500',
  offline: 'bg-gray-400',
  busy: 'bg-error-500',
  away: 'bg-warning-500',
}

/**
 * Fallback background colors for initials (using brand and semantic palette)
 */
const fallbackColors = [
  'bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300',
  'bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
]

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get initials from a name (up to 2 characters)
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 0 || !parts[0]) return '?'
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Generate a consistent background color based on the name
 * Uses a simple hash function to ensure same name always gets same color
 */
function getFallbackColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return fallbackColors[Math.abs(hash) % fallbackColors.length]
}

// ============================================================================
// Avatar Props
// ============================================================================

export interface AvatarProps {
  /** Image source URL */
  src?: string | null
  /** Alt text for the image */
  alt?: string
  /** Name to generate initials from (used as fallback when no image) */
  name?: string
  /** Size variant */
  size?: AvatarSize
  /** Shape variant */
  shape?: AvatarShape
  /** Whether to show a status indicator dot */
  showStatus?: boolean
  /** Status color (online, offline, busy, away) */
  status?: AvatarStatus
  /** Whether to show a verification badge */
  verified?: boolean
  /** Adds a contrast border (useful when avatar is on colored backgrounds) */
  contrastBorder?: boolean
  /** Additional CSS classes */
  className?: string
}

// ============================================================================
// Avatar Component
// ============================================================================

/**
 * Avatar - Untitled UI Avatar Component
 *
 * Displays a user avatar with image support and fallback to initials.
 * Features status indicators, verification badges, and multiple sizes.
 *
 * @example
 * // Basic usage with image
 * <Avatar src="/user.jpg" alt="John Doe" />
 *
 * @example
 * // Fallback to initials
 * <Avatar name="John Doe" size="lg" />
 *
 * @example
 * // With status indicator
 * <Avatar name="Jane Smith" showStatus status="online" />
 *
 * @example
 * // Verified user
 * <Avatar src="/user.jpg" name="John Doe" verified />
 *
 * @example
 * // Different sizes
 * <Avatar name="User" size="xxs" />
 * <Avatar name="User" size="xs" />
 * <Avatar name="User" size="sm" />
 * <Avatar name="User" size="md" />
 * <Avatar name="User" size="lg" />
 * <Avatar name="User" size="xl" />
 * <Avatar name="User" size="2xl" />
 */
export function Avatar({
  src,
  alt,
  name = '',
  size = 'md',
  shape = 'circle',
  showStatus = false,
  status = 'offline',
  verified = false,
  contrastBorder = false,
  className,
}: AvatarProps) {
  const { t } = useTranslation()
  const [imageError, setImageError] = useState(false)

  const styles = sizeStyles[size]
  const showImage = src && !imageError
  const initials = name ? getInitials(name) : ''
  const fallbackColor = name
    ? getFallbackColor(name)
    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'

  return (
    <div className={cn('relative inline-flex shrink-0', className)}>
      {/* Main avatar container */}
      {showImage ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className={cn(
            'object-cover',
            styles.container,
            shapeStyles[shape],
            contrastBorder && 'ring-2 ring-white dark:ring-gray-900'
          )}
          onError={() => setImageError(true)}
        />
      ) : initials ? (
        <div
          className={cn(
            'flex items-center justify-center',
            styles.container,
            styles.text,
            shapeStyles[shape],
            fallbackColor,
            contrastBorder && 'ring-2 ring-white dark:ring-gray-900'
          )}
          title={name || undefined}
        >
          {initials}
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center justify-center bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
            styles.container,
            shapeStyles[shape],
            contrastBorder && 'ring-2 ring-white dark:ring-gray-900'
          )}
        >
          <User01Icon className={styles.icon} />
        </div>
      )}

      {/* Status indicator */}
      {showStatus && (
        <span
          className={cn(
            'absolute rounded-full ring-white dark:ring-gray-900',
            styles.status,
            styles.statusOffset,
            statusColors[status]
          )}
          aria-label={t('components.avatar.status_label', { status })}
        />
      )}

      {/* Verification badge */}
      {verified && (
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 rounded-full bg-white dark:bg-gray-900',
            size === 'xxs' || size === 'xs' ? 'p-0' : 'p-0.5'
          )}
        >
          <CheckVerified02Icon
            className={cn(
              'text-brand-500',
              size === 'xxs' || size === 'xs' ? 'h-2 w-2' : size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'
            )}
          />
        </span>
      )}
    </div>
  )
}

// ============================================================================
// Avatar Group Component
// ============================================================================

export interface AvatarGroupProps {
  /** Array of avatar data */
  avatars: Array<{
    src?: string | null
    name?: string
    alt?: string
  }>
  /** Maximum number of avatars to show */
  max?: number
  /** Size of avatars in the group */
  size?: AvatarSize
  /** Additional CSS classes */
  className?: string
}

/**
 * AvatarGroup - Stacked avatar group component
 *
 * Displays multiple avatars in a stacked layout with optional "+N" overflow indicator.
 *
 * @example
 * <AvatarGroup
 *   avatars={[
 *     { src: '/user1.jpg', name: 'John Doe' },
 *     { name: 'Jane Smith' },
 *     { src: '/user3.jpg', name: 'Bob Wilson' },
 *   ]}
 *   max={3}
 * />
 */
export function AvatarGroup({
  avatars,
  max = 5,
  size = 'sm',
  className,
}: AvatarGroupProps) {
  const visibleAvatars = avatars.slice(0, max)
  const overflowCount = avatars.length - max

  const overlapStyles: Record<AvatarSize, string> = {
    xxs: '-ml-1',
    xs: '-ml-1.5',
    sm: '-ml-2',
    md: '-ml-2.5',
    lg: '-ml-3',
    xl: '-ml-3.5',
    '2xl': '-ml-4',
  }

  return (
    <div className={cn('flex items-center', className)}>
      {visibleAvatars.map((avatar, index) => (
        <div
          key={index}
          className={cn(index > 0 && overlapStyles[size])}
          style={{ zIndex: visibleAvatars.length - index }}
        >
          <Avatar
            src={avatar.src}
            name={avatar.name}
            alt={avatar.alt}
            size={size}
            contrastBorder
          />
        </div>
      ))}
      {overflowCount > 0 && (
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-gray-100 ring-2 ring-white dark:bg-gray-700 dark:ring-gray-900',
            sizeStyles[size].container,
            sizeStyles[size].text,
            'text-gray-600 dark:text-gray-300',
            overlapStyles[size]
          )}
          style={{ zIndex: 0 }}
        >
          +{overflowCount}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Avatar Label Component (for user displays with text)
// ============================================================================

export interface AvatarLabelProps {
  /** Image source URL */
  src?: string | null
  /** User's name */
  name: string
  /** Secondary text (email, role, etc.) */
  secondaryText?: string
  /** Size of the avatar */
  size?: 'sm' | 'md' | 'lg'
  /** Status indicator */
  status?: AvatarStatus
  /** Whether to show verification badge */
  verified?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * AvatarLabel - Avatar with accompanying name and description
 *
 * Displays an avatar alongside user name and optional secondary text.
 * Useful for user lists, dropdowns, and profile previews.
 *
 * @example
 * <AvatarLabel
 *   src="/user.jpg"
 *   name="John Doe"
 *   secondaryText="john@example.com"
 *   verified
 * />
 */
export function AvatarLabel({
  src,
  name,
  secondaryText,
  size = 'md',
  status,
  verified = false,
  className,
}: AvatarLabelProps) {
  const avatarSize: AvatarSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'md'

  const textStyles: Record<'sm' | 'md' | 'lg', { name: string; secondary: string; gap: string }> = {
    sm: {
      name: 'text-sm font-medium',
      secondary: 'text-xs',
      gap: 'gap-2',
    },
    md: {
      name: 'text-sm font-medium',
      secondary: 'text-sm',
      gap: 'gap-3',
    },
    lg: {
      name: 'text-base font-medium',
      secondary: 'text-sm',
      gap: 'gap-3',
    },
  }

  const styles = textStyles[size]

  return (
    <div className={cn('flex items-center', styles.gap, className)}>
      <Avatar
        src={src}
        name={name}
        size={avatarSize}
        showStatus={!!status}
        status={status}
        verified={verified}
      />
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-gray-900 dark:text-gray-100', styles.name)}>{name}</p>
        {secondaryText && (
          <p className={cn('truncate text-gray-500 dark:text-gray-400', styles.secondary)}>
            {secondaryText}
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Exports
// ============================================================================

export default Avatar
